import { Auth0ContextInterface, useAuth0 /* User */ } from '@auth0/auth0-react';
import { getConfig } from 'helpers/appConfig';
import { useAuth as useAuthKC } from 'react-oidc-context';

const config = getConfig();

let _token: string = localStorage.getItem('debug_token') ?? '';

interface AuthIdentity {
  ssouser: boolean;
  email?: string;
  sub?: string;
  email_verified?: boolean;
}

interface UniversalAuthenticationInterface {
  token?: string | undefined;
  /** Equivalent to auth0's getAccessTokenSilently */
  initializeAccessTokenFromStorage: (opts?: {
    ignoreCache: boolean;
  }) => Promise<string | undefined>;

  /** logout */
  logout: (opts?: { returnTo: string }) => void;

  isLoading: boolean;
  isAuthenticated: boolean;
  user?: AuthIdentity;
  error?: string | object;
}

export const debugAuthContext: Auth0ContextInterface<AuthIdentity> &
  UniversalAuthenticationInterface = {
  get token() {
    return _token ?? localStorage.getItem('debug_token') ?? '';
  },
  set token(t: string | undefined) {
    _token = t ?? '';
    localStorage.setItem('debug_token', t ?? '');
  },
  initializeAccessTokenFromStorage() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Promise.resolve(_token as any);
  },
  getAccessTokenSilently() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Promise.resolve(_token as any);
  },
  getAccessTokenWithPopup() {
    throw new Error('not implemented');
  },
  getIdTokenClaims() {
    throw new Error('not implemented');
  },
  loginWithRedirect() {
    throw new Error('not implemented');
  },
  loginWithPopup() {
    throw new Error('not implemented');
  },
  buildAuthorizeUrl() {
    throw new Error('not implemented');
  },
  buildLogoutUrl() {
    throw new Error('not implemented');
  },
  handleRedirectCallback() {
    throw new Error('not implemented');
  },
  async logout() {
    _token = '';
    localStorage.setItem('debug_token', '');
    global.history.pushState(null, '', '/login');
  },
  isAuthenticated: true,
  isLoading: false,
  user: {
    ssouser: false,
  },
};

const debugAuthentication = () => debugAuthContext;

const useAuth0Authentication = () => {
  const auth0Context = useAuth0<AuthIdentity>();

  const context = {
    ...auth0Context,
    initializeAccessTokenFromStorage: auth0Context.getAccessTokenSilently,
  };

  return context;
};

const useKeycloakAuthentication: () => UniversalAuthenticationInterface =
  () => {
    const kcContext = useAuthKC();

    const context = {
      ...kcContext,
      get token() {
        return kcContext.user?.access_token;
      },
      initializeAccessTokenFromStorage: async () => {
        // https://github.com/authts/oidc-client-ts/blob/main/docs/protocols/refresh-token-grant.md
        const result = await kcContext.signinSilent();
        return result?.access_token;
      },
      async logout() {
        return await Promise.all([
          kcContext.removeUser(),
          kcContext.signoutSilent(),
          kcContext.revokeTokens(),
        ]);
      },
      user: kcContext.user
        ? {
            ssouser: false,
            sub: kcContext.user.profile.sub,
            email: kcContext.user.profile.email,
            email_verified: kcContext.user.profile.email_verified,
          }
        : undefined,
    };

    return context;
  };

export const useAuthentication: () => UniversalAuthenticationInterface =
  AUTH_BYPASS
    ? debugAuthentication
    : config.keycloak.enabled
      ? useKeycloakAuthentication
      : useAuth0Authentication;
